import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowUp, ArrowDown, BookOpen, X, RefreshCw, Download } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Ledger({ inventoryTypeLock = null, title = 'Stock Ledger', description = 'Complete audit trail of all inbound and outbound movements' }) {
  const [entries, setEntries] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    entry_type: 'all',
    inventory_type: inventoryTypeLock || 'all',
    product_id: 'all',
    category_id: 'all',
    date_from: '',
    date_to: '',
  });

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.entry_type !== 'all') params.append('entry_type', filters.entry_type);
      if (filters.inventory_type !== 'all') params.append('inventory_type', filters.inventory_type);
      if (filters.product_id !== 'all') params.append('product_id', filters.product_id);
      if (filters.category_id !== 'all') params.append('category_id', filters.category_id);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to + 'T23:59:59Z');

      const response = await axios.get(`${API}/stock-adjustments?${params.toString()}`);
      setEntries(response.data);
    } catch (error) {
      console.error('Failed to fetch ledger:', error);
      toast.error('Failed to load ledger');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchReferenceData = useCallback(async () => {
    try {
      const [prodsRes, catsRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/categories?active_only=false`),
      ]);
      setProducts(prodsRes.data);
      setCategories(catsRes.data);
    } catch (error) {
      console.error('Failed to fetch reference data', error);
    }
  }, []);

  useEffect(() => {
    fetchReferenceData();
  }, [fetchReferenceData]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const resetFilters = () => {
    setFilters({
      entry_type: 'all',
      inventory_type: inventoryTypeLock || 'all',
      product_id: 'all',
      category_id: 'all',
      date_from: '',
      date_to: '',
    });
  };

  const [exporting, setExporting] = useState(false);

  const downloadCsv = async () => {
    setExporting(true);
    try {
      // Re-issue the query with a very high limit so the export contains every
      // row matching the current filter, not just what's on screen.
      const params = new URLSearchParams();
      if (filters.entry_type !== 'all') params.append('entry_type', filters.entry_type);
      if (filters.inventory_type !== 'all') params.append('inventory_type', filters.inventory_type);
      if (filters.product_id !== 'all') params.append('product_id', filters.product_id);
      if (filters.category_id !== 'all') params.append('category_id', filters.category_id);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to + 'T23:59:59Z');
      params.append('limit', '100000');

      const res = await axios.get(`${API}/stock-adjustments?${params.toString()}`);
      const rows = res.data || [];
      if (rows.length === 0) {
        toast.info('Nothing to export — no entries match the current filters');
        return;
      }

      const headers = [
        'Date', 'Time', 'SKU', 'Product', 'Category', 'Inventory Type',
        'Movement', 'Quantity', 'Unit Price', 'Total Cost',
        'Reason', 'Order Number', 'Requestor', 'Supplier', 'Performed By', 'Notes',
      ];

      const escape = (v) => {
        if (v === null || v === undefined) return '';
        const s = String(v);
        // Quote values containing quote, comma, newline, or leading = (Excel formula injection guard).
        if (/[",\n\r]/.test(s) || /^[=+\-@]/.test(s)) {
          return `"${s.replace(/"/g, '""')}"`;
        }
        return s;
      };

      const lines = [headers.join(',')];
      for (const e of rows) {
        const dt = e.created_at ? new Date(e.created_at) : null;
        const date = dt ? dt.toISOString().slice(0, 10) : '';
        const time = dt ? dt.toISOString().slice(11, 19) : '';
        const movement = e.adjustment_type === 'in' ? 'Inbound'
          : e.adjustment_type === 'out' ? 'Outbound'
          : e.adjustment_type === 'adjustment' ? 'Correction'
          : (e.adjustment_type || '');
        const qty = e.adjustment_type === 'out'
          ? -Math.abs(e.quantity ?? 0)
          : (e.quantity ?? 0);
        lines.push([
          date, time, e.product_sku, e.product_name, e.category,
          e.inventory_type === 'internal' ? 'Internal' : 'Customer',
          movement, qty,
          e.unit_price ?? '', e.total_cost ?? '',
          (e.reason || '').replace(/_/g, ' '),
          e.order_number, e.requestor, e.supplier, e.performed_by, e.notes,
        ].map(escape).join(','));
      }

      // Prepend BOM so Excel opens UTF-8 correctly.
      const blob = new Blob(['\ufeff', lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `ledger-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${rows.length} entr${rows.length === 1 ? 'y' : 'ies'}`);
    } catch (err) {
      console.error('CSV export failed', err);
      toast.error('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const inboundEntries = entries.filter((e) => e.adjustment_type === 'in');
  const outboundEntries = entries.filter((e) => e.adjustment_type === 'out');
  const adjustmentEntries = entries.filter((e) => e.adjustment_type === 'adjustment');

  const filteredProducts = filters.inventory_type === 'all'
    ? products
    : products.filter((p) => p.inventory_type === filters.inventory_type);

  const filteredCategories = filters.inventory_type === 'all'
    ? categories
    : categories.filter((c) => c.inventory_type === filters.inventory_type || c.inventory_type === 'both');

  const renderTable = (rows, emptyMessage) => {
    if (rows.length === 0) {
      return (
        <div className="text-center py-12 text-slate-500">{emptyMessage}</div>
      );
    }
    return (
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date &amp; Time</TableHead>
              <TableHead>SKU</TableHead>
              <TableHead>Product</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Qty</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Reference</TableHead>
              <TableHead>By</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((entry) => (
              <TableRow key={entry.id} className="table-row-hover">
                <TableCell className="text-sm whitespace-nowrap">
                  {new Date(entry.created_at).toLocaleString(undefined, {
                    year: 'numeric',
                    month: 'short',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </TableCell>
                <TableCell>
                  <code className="font-mono text-xs bg-slate-100 px-2 py-1 rounded">
                    {entry.product_sku}
                  </code>
                </TableCell>
                <TableCell className="font-medium max-w-xs truncate">{entry.product_name}</TableCell>
                <TableCell className="text-sm">
                  {entry.category ? (
                    <Badge variant="outline" className="text-xs">{entry.category}</Badge>
                  ) : (
                    <span className="text-slate-400">-</span>
                  )}
                </TableCell>
                <TableCell>
                  {entry.inventory_type === 'internal' ? (
                    <Badge className="bg-teal-100 text-teal-800 hover:bg-teal-100 text-xs">Internal</Badge>
                  ) : (
                    <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100 text-xs">Customer</Badge>
                  )}
                </TableCell>
                <TableCell className="font-mono font-semibold">
                  {entry.adjustment_type === 'in' && '+'}
                  {entry.adjustment_type === 'out' && '-'}
                  {entry.quantity}
                </TableCell>
                <TableCell className="text-sm capitalize">
                  {entry.reason?.replace(/_/g, ' ') || '-'}
                </TableCell>
                <TableCell className="text-sm">
                  {entry.order_number ? (
                    <span className="text-slate-700">
                      Order <code className="bg-slate-100 px-1 rounded">{entry.order_number}</code>
                    </span>
                  ) : entry.requestor ? (
                    <span className="text-slate-700">Req: <strong>{entry.requestor}</strong></span>
                  ) : entry.supplier ? (
                    <span className="text-slate-700">Sup: {entry.supplier}</span>
                  ) : (
                    <span className="text-slate-400">-</span>
                  )}
                </TableCell>
                <TableCell className="text-sm text-slate-600">{entry.performed_by || '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="ledger-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900">{title}</h1>
          <p className="text-slate-600 mt-2">{description}</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={downloadCsv}
            variant="outline"
            className="gap-2"
            disabled={exporting || loading}
            data-testid="download-ledger-csv-button"
          >
            <Download className="h-4 w-4" />
            {exporting ? 'Exporting…' : 'Download CSV'}
          </Button>
          <Button
            onClick={fetchEntries}
            variant="outline"
            className="gap-2"
            data-testid="refresh-ledger-button"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex justify-between items-center">
            <CardTitle className="text-base">Filters</CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={resetFilters}
              className="gap-1 text-slate-600"
              data-testid="reset-filters-button"
            >
              <X className="h-3.5 w-3.5" />
              Clear
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className={`grid grid-cols-2 md:grid-cols-3 ${inventoryTypeLock ? 'lg:grid-cols-5' : 'lg:grid-cols-6'} gap-3`}>
            <div className="space-y-1">
              <Label className="text-xs">Movement</Label>
              <Select
                value={filters.entry_type}
                onValueChange={(v) => setFilters({ ...filters, entry_type: v })}
              >
                <SelectTrigger data-testid="filter-entry-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="in">Inbound</SelectItem>
                  <SelectItem value="out">Outbound</SelectItem>
                  <SelectItem value="adjustment">Corrections</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {!inventoryTypeLock && (
              <div className="space-y-1">
                <Label className="text-xs">Inventory</Label>
                <Select
                  value={filters.inventory_type}
                  onValueChange={(v) => setFilters({ ...filters, inventory_type: v, product_id: 'all', category_id: 'all' })}
                >
                  <SelectTrigger data-testid="filter-inventory-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="customer">Customer</SelectItem>
                    <SelectItem value="internal">Internal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1">
              <Label className="text-xs">Category</Label>
              <Select
                value={filters.category_id}
                onValueChange={(v) => setFilters({ ...filters, category_id: v })}
              >
                <SelectTrigger data-testid="filter-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {filteredCategories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name} ({cat.prefix})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Product</Label>
              <Select
                value={filters.product_id}
                onValueChange={(v) => setFilters({ ...filters, product_id: v })}
              >
                <SelectTrigger data-testid="filter-product"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Products</SelectItem>
                  {filteredProducts.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.sku} · {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">From</Label>
              <Input
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                data-testid="filter-date-from"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">To</Label>
              <Input
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                data-testid="filter-date-to"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary counts */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="bg-green-50 p-2 rounded-lg">
              <ArrowUp className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-xs text-slate-600">Inbound</p>
              <p className="text-2xl font-bold text-slate-900">{inboundEntries.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="bg-red-50 p-2 rounded-lg">
              <ArrowDown className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-xs text-slate-600">Outbound</p>
              <p className="text-2xl font-bold text-slate-900">{outboundEntries.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="bg-blue-50 p-2 rounded-lg">
              <BookOpen className="h-5 w-5 text-[#1680e5]" />
            </div>
            <div>
              <p className="text-xs text-slate-600">Corrections</p>
              <p className="text-2xl font-bold text-slate-900">{adjustmentEntries.length}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Ledger Table */}
      <Card>
        <CardHeader>
          <CardTitle>
            {filters.entry_type === 'in' ? 'Inbound Entries' :
             filters.entry_type === 'out' ? 'Outbound Entries' :
             filters.entry_type === 'adjustment' ? 'Corrections' :
             'All Movements'}
          </CardTitle>
          <CardDescription>{entries.length} entr{entries.length === 1 ? 'y' : 'ies'} found</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1680e5]"></div>
            </div>
          ) : (
            renderTable(entries, 'No ledger entries match the current filters')
          )}
        </CardContent>
      </Card>
    </div>
  );
}
